import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { translateError } from '../../utils/errorMessages';
import { Button, EmptyState, LoadingSpinner } from '../../components/ui';
import './ServerErrorsPage.css';

/**
 * Every 500 the API answers is written to `public.audit_logs` with
 * `category: 'error'` and a short reference id (see backend errorHandler).
 * This page is the read side: without it those rows are only reachable by
 * querying the database, and the product has no external error tracker.
 *
 * The user never sees the reference — it exists so a report of "it broke around
 * two o'clock" can be tied to one row and one log line.
 */

interface ServerErrorLog {
  _id: number;
  action: string;
  userEmail: string | null;
  userName: string | null;
  organizationSlug: string | null;
  orgName: string | null;
  errorMessage: string | null;
  ipAddress: string | null;
  createdAt: string;
  details?: {
    reference?: string;
    method?: string;
    path?: string;
    name?: string;
  } | null;
}

const PAGE_SIZE = 50;

const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'medium' });

const ServerErrorsPage: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<ServerErrorLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.superAdmin.auditLogs.list({
        category: 'error',
        page,
        limit: PAGE_SIZE,
        ...(appliedSearch ? { search: appliedSearch } : {}),
      });
      setLogs(data.logs ?? []);
      setTotal(data.pagination?.total ?? 0);
    } catch (err) {
      setError(translateError(err, t, 'serverErrors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, t]);

  useEffect(() => {
    void fetchLogs();
  }, [fetchLogs]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const toggle = (id: number) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  return (
    <div className="server-errors">
      <header className="server-errors__header">
        <div>
          <h1>{t('serverErrors.title')}</h1>
          <p className="server-errors__subtitle">{t('serverErrors.subtitle')}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void fetchLogs()} loading={loading}>
          {t('common.refresh')}
        </Button>
      </header>

      <form className="server-errors__search" onSubmit={submitSearch}>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('serverErrors.searchPlaceholder')}
          aria-label={t('serverErrors.searchPlaceholder')}
        />
        <Button type="submit" size="sm">{t('common.search')}</Button>
        {appliedSearch && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { setSearch(''); setAppliedSearch(''); setPage(1); }}
          >
            {t('common.clear')}
          </Button>
        )}
      </form>

      {error && <p className="error-message">{error}</p>}

      {loading && logs.length === 0 ? (
        <LoadingSpinner text={t('common.loading')} />
      ) : logs.length === 0 ? (
        <EmptyState
          title={t('serverErrors.emptyTitle')}
          description={appliedSearch ? t('serverErrors.emptySearch') : t('serverErrors.emptyDescription')}
        />
      ) : (
        <>
          <p className="server-errors__count">{t('serverErrors.count', { count: total })}</p>
          <div className="table-container">
            <table className="server-errors__table">
              <thead>
                <tr>
                  <th>{t('serverErrors.when')}</th>
                  <th>{t('serverErrors.reference')}</th>
                  <th>{t('serverErrors.request')}</th>
                  <th>{t('serverErrors.organization')}</th>
                  <th>{t('serverErrors.user')}</th>
                  <th aria-label={t('serverErrors.detail')} />
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const isOpen = expanded.has(log._id);
                  return (
                    <React.Fragment key={log._id}>
                      <tr>
                        <td className="server-errors__when">{formatDateTime(log.createdAt)}</td>
                        <td><code className="server-errors__ref">{log.details?.reference ?? '—'}</code></td>
                        <td className="server-errors__request">
                          <span className="server-errors__method">{log.details?.method ?? '—'}</span>
                          {' '}
                          {log.details?.path ?? '—'}
                        </td>
                        <td>{log.orgName ?? log.organizationSlug ?? '—'}</td>
                        <td>{log.userEmail ?? '—'}</td>
                        <td className="server-errors__toggle">
                          <Button variant="ghost" size="sm" onClick={() => toggle(log._id)}>
                            {isOpen ? t('serverErrors.hide') : t('serverErrors.detail')}
                          </Button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="server-errors__detail-row">
                          <td colSpan={6}>
                            <dl>
                              <dt>{t('serverErrors.exception')}</dt>
                              <dd><code>{log.details?.name ?? '—'}</code></dd>
                              <dt>{t('serverErrors.message')}</dt>
                              <dd><pre>{log.errorMessage ?? '—'}</pre></dd>
                              <dt>{t('serverErrors.ip')}</dt>
                              <dd>{log.ipAddress || '—'}</dd>
                            </dl>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <nav className="server-errors__pagination" aria-label={t('serverErrors.pagination')}>
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('common.previous')}
              </Button>
              <span>{t('serverErrors.page', { page, totalPages })}</span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                {t('common.next')}
              </Button>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default ServerErrorsPage;
